import cx from 'classnames'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { MdClose, MdDownload, MdOutlineFileUpload } from 'react-icons/md'
import { useParams } from 'react-router-dom'

import { getIdToken } from '../../../../../lib/cognito'
import Document from '../../../../shared/DocumentViewer/document'
import styles from '../note.module.scss'

const FILE_TYPES = [ 'image/jpeg', 'image/gif', 'image/png', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ]

function round (value: number, precision = 2): number {
  let k = Math.pow(10, precision)
  return Math.round(value * k) / k
}

export function humanReadableBytes (bytes: number): string {
  let i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${round(bytes / Math.pow(1024, i))} ${[ 'B', 'KB', 'MB', 'GB', 'TB' ][i] ?? 'N/A'}`
}

interface DocumentsUploaderProps {
  file: File | null
  setFile: (file: File | null) => void
}

interface PreviewProps {
  base64?: string
  type?: string
  name?: string
  id?: string
}

function Preview ({ base64, type, name, id }: PreviewProps): JSX.Element {
  if ([ 'application/pdf', 'image/jpeg', 'image/png', 'image/gif' ].includes(type ?? '')) {
    return (
      <div className='relative bg-[#525659] h-[275px]'>
        <Document
          id={id}
          mime={type}
          data={base64?.startsWith('data:') ? base64.split(',')[1] : base64}
          fileName={name}
          style={{
            height: '100%'
          }}
        />
      </div>
    )
  }

  return (
    <div className='relative bg-[#525659] flex justify-center items-center h-[275px]'>
      <p className='text-white'>No preview available</p>
    </div>
  )
}

export function DocumentsUploader ({ file, setFile }: DocumentsUploaderProps): JSX.Element {
  let fileInputRef = useRef<HTMLInputElement>(null)
  let [ fileBase64, setFileBase64 ] = useState<string | ArrayBuffer | null>(null)
  let [ isDraggingOver, setIsDraggingOver ] = useState(false)
  async function getBase64 (file: File): Promise<string | ArrayBuffer | null> {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        resolve(reader.result)
      }
      reader.onerror = reject
    })
  }

  useEffect(() => {
    if (!file) {
      return
    }
    getBase64(file)
      .then(res => setFileBase64(res)) // `res` base64 of img file
      .catch(err => console.error(err))
  }, [ file ])

  let handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()

    setIsDraggingOver(true)
  }, [])

  let handleDragEnter = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()

    setIsDraggingOver(true)
  }, [])

  let handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()

    setIsDraggingOver(false)
  }, [])

  let handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()

    let file = event.dataTransfer.files?.[0]

    if (!file) {
      return
    }

    if (!FILE_TYPES.includes(file.type)) {
      return
    }

    setFile(file)
    setIsDraggingOver(false)
  }, [ setFile ])

  return (
    <div className='h-full flex flex-col gap-[5px]'>
      <div className='flex flex-row justify-between items-center gap-20'>
        <span className={styles.inputLabel} >
          Attachment <span style={{ position: 'absolute', top: -3, right: -7, fontSize: '1rem', color: 'red' }}>*</span>
        </span>

        {file && (
          <div className='flex flex-row items-center gap-4'>
            <div className='flex flex-row items-center gap-1'>
              <span className={cx(styles.inputLabel, 'text-gray-900 line-clamp-1')}>
                {file?.name}
              </span>
              <span className={cx(styles.inputLabel, 'text-gray-400')}>
                {humanReadableBytes(file?.size ?? 0)}
              </span>
            </div>
            <button
              onClick={() => setFile(null)}
              className='text-gray-400 hover:text-gray-900 cursor-pointer w-4 h-4'
            >
              <MdClose
                size='1rem'
                color='inherit'
              />
            </button>
          </div>
        )}
      </div>

      {file && (
        <Preview
          base64={(typeof fileBase64 === 'string' ? fileBase64 : fileBase64?.toString()) ?? ''}
          type={file.type}
          name={file.name}
        />
      )}
      <div
        className={cx('relative', [ !file && 'h-full' ])}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
      >
        <input
          type='file'
          accept={FILE_TYPES.join(', ')}
          ref={fileInputRef}
          className='absolute left-0 w-full opacity-0 z-10 h-full cursor-pointer'
          onChange={event => {
            if (event.currentTarget.files?.[0]) {
              setFile(event.currentTarget.files?.[0] ?? null)
            }
          }}
        />

        {!file && (
          <button
            className={cx('w-full h-[275px] flex flex-col items-center justify-center gap-3 rounded-xl relative transition-colors border-dashed border-[3px] border-gray-200', {
              'border-dashed border-4 border-gray-300 bg-gray-100': isDraggingOver,
              'rounded-b-none': file
            })}
            type='button'
            data-cy='drop-target'
          >
            <MdOutlineFileUpload
              size={44}
              className={cx('text-sm font-normal text-gray-400 flex-shrink-0')}
            />

            <span className='text-base font-normal text-gray-400'>
              {!isDraggingOver && (
                <span><span className='font-bold'>Choose a file </span>or drag it here</span>
              )}
              {isDraggingOver && (
                <span>Drop file here</span>
              )}
            </span>

            <input
              type='text'
              className='w-[1px] h-[1px] opacity-0 m-0 p-0 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2'
              required
              onInvalid={(e) => (e.target as HTMLInputElement).setCustomValidity('Attachment is required')}
            />
          </button>
        )}
      </div>
    </div>
  )
}

interface DocumentViewerProps {
  fileId?: string
  fileType?: string
  fileName?: string
  fileBase64?: string
  fileSize?: number
}

const openUrlInNewTab = (url: string): void => {
  // @ts-expect-error //it's okay
  getIdToken().then(idToken => {
    const sing = url.includes('?') ? '&' : '?'
    window.open(`${url}${sing}authorization=Bearer ${idToken as string}`, '_blank')
  })
}

export function DocumentViewer ({ fileId, fileBase64, fileType, fileName, fileSize }: DocumentViewerProps): JSX.Element {
  const { id, clientName } = useParams()

  const link = `/api/patients/${id ?? ''}/documents/${fileId ?? ''}?documentType=DocumentReference&clientName=${clientName ?? ''}&downloadType=inline`
  return (
    <div className='h-full flex flex-col gap-[5px]'>
      <div className='flex flex-row justify-between items-center gap-20'>
        <span className={styles.inputLabel}>
          Attachment <span style={{ position: 'absolute', top: -3, right: -7, fontSize: '1rem', color: 'red' }}>*</span>
        </span>

        {fileName && (
          <div className='flex flex-row items-center gap-4'>
            <div className='flex flex-row items-center gap-1'>
              <span className={cx(styles.inputLabel, 'text-gray-900 line-clamp-1')}>
                {fileName}
              </span>
              <span className={cx(styles.inputLabel, 'text-gray-400')}>
                {humanReadableBytes(fileSize ?? 0)}
              </span>
            </div>
            <button
              type='button'
              onClick={() => openUrlInNewTab(link)}
              data-test-id='behavioral-download-document'
              className='text-gray-400 hover:text-gray-900 cursor-pointer w-4 h-4'
            >
              <MdDownload
                size='1rem'
                color='inherit'
              />
            </button>
          </div>
        )}
      </div>
      <div className='relative bg-[#525659] h-[275px]'>
        <Preview
          id={fileId}
          type={fileType}
          name={fileName}
        />
      </div>
    </div>
  )
}
